import { types } from "api";
import { isValidEmail } from "helpers/authValidation";
import { getRowsFromFiles, MultipleFileResults } from "./csv";

export const INCOMPLETE_OR_INVALID_FORMAT =
    "Account information is not completed or in a wrong format!";

export const INVALID_ROLE =
    "Roles must be either 'admin', 'admin-teacher', 'teacher', 'student', or 'parent'.";

export const IMPORT_USERS_CSV_TIPS =
    "Upload a CSV with columns: firstName, lastName, email, school, password. All accounts are imported as students. Download the template to get started.";

export const SCHOOL_MISMATCH = "School must be Swinburne University of Technology";
export const PASSWORD_REQUIRED = "Password is required";

const SWINBURNE = "swinburne university of technology";

const HEADER_FIRST_FOUR = ["firstname", "lastname", "email", "school"];
const HEADER_PWD_VARIANTS = ["password", "pwd"];

export type CsvUserRow = types.CreateUserRequest & { userId: string };

const isHeaderRow = (item: string[]): boolean => {
    if (item.length !== 5) return false;
    const first4Match = HEADER_FIRST_FOUR.every(
        (h, i) => (item[i] ?? "").toLowerCase().trim() === h,
    );
    const pwdMatch = HEADER_PWD_VARIANTS.includes(
        (item[4] ?? "").toLowerCase().trim(),
    );
    return first4Match && pwdMatch;
};

export const toUser =
    (getNow: () => number = Date.now) =>
    (item: unknown): { row: CsvUserRow | null; error?: string } => {
        if (!Array.isArray(item)) {
            return { row: null, error: INCOMPLETE_OR_INVALID_FORMAT };
        }

        if (item.length === 0 || (item.length === 1 && item[0] === "")) {
            return { row: null };
        }

        if (item.length < 5) {
            return { row: null, error: INCOMPLETE_OR_INVALID_FORMAT };
        }

        // Skip header row if the file was downloaded from the template
        if (isHeaderRow(item as string[])) {
            return { row: null };
        }

        let [firstName, lastName, email, school, password] = item as string[];
        firstName = firstName?.trim();
        lastName = lastName?.trim();
        email = email?.trim();
        school = school?.trim();
        password = password?.trim().replace(/^"|"$/g, "");

        if (!firstName || !lastName || !email) {
            return { row: null, error: INCOMPLETE_OR_INVALID_FORMAT };
        }

        if (!isValidEmail(email)) {
            return { row: null, error: `"${email}" is an invalid email address` };
        }

        if (school.toLowerCase() !== SWINBURNE) {
            return { row: null, error: SCHOOL_MISMATCH };
        }

        if (!password) {
            return { row: null, error: PASSWORD_REQUIRED };
        }

        return {
            row: {
                firstName,
                lastName,
                email,
                role: ["student"],
                password,
                userId: getNow().toString(),
            },
        };
    };

export const getUsersFromCSVs = (
    files: File[],
    getNow: () => number = Date.now,
): Promise<MultipleFileResults<CsvUserRow>> => {
    return getRowsFromFiles(files, toUser(getNow));
};
